// packages/core/src/components/schedule-v2/ScheduleGridV2.tsx
//
// La grille de l'éditeur d'horaires V2 — Docs/SPECS/2026-09-12-editeur-
// horaires-v2-decisions.md §5b (D33). Un seul conteneur de défilement, une
// seule grille CSS, trois zones collantes (angle, en-têtes de jours,
// colonne des heures), plages positionnées en absolu au prorata. C'est
// exactement la structure validée par le spike mesuré
// (Docs/SPECS/spike-grille-horaires-v2/) : 35 contrôles d'alignement à
// 0,01 px près après défilement et 5 changements de pas.
//
// Pourquoi V1 (`TimeSlotsTable`, packages/core/src/components/time-slots/)
// n'a jamais garanti cet alignement (audit §9) : deux arbres flex
// synchronisés par des hauteurs calibrées à la main. Ici il n'y a qu'UN
// arbre (`display: grid`) et une seule formule de position
// (`schedule/geometry.ts::topForMinute`), appliquée identiquement aux
// lignes de fond, aux étiquettes d'heure et aux plages — elles ne peuvent
// pas diverger (D33n).
import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { localeTag } from "@core/i18n";
import {
  centerMinuteFromScroll,
  computeGridDimensions,
  rowCount,
  scrollTopToKeepCentered,
} from "../../schedule/geometry";
import type { DayRangesMap, Range, Step } from "../../schedule/types";
import {
  usePaintGesture,
  type MultiColumnPaintResult,
  type ScheduleSelection,
} from "./usePaintGesture";

/** Hauteur de la ligne d'en-têtes de jours, collante en haut du conteneur. */
const HEADER_HEIGHT = 44;

export interface ScheduleGridV2Handle {
  /**
   * Défile pour centrer `minute` dans la zone visible (D34f, D33k/o).
   * Doux par défaut (D33k) ; `"instant"` pour l'ancrage au changement de pas
   * (D33o), qui doit être appliqué avant le premier affichage.
   */
  scrollToMinutes: (minute: number, behavior?: ScrollBehavior) => void;
  /** Minute au centre de la zone visible, lue sur le DOM courant (D33o). */
  getCenterMinute: () => number | null;
}

export interface ScheduleGridV2Props {
  /** Dates à afficher en colonnes — déjà filtrées à 1 par le pager mobile (D24) le cas échéant. */
  dates: string[];
  dayRanges: DayRangesMap;
  step: Step;
  visibleStartMinute: number;
  visibleEndMinute: number;
  selected: ScheduleSelection | null;
  onSelect: (selection: ScheduleSelection | null) => void;
  onRangesChange: (date: string, ranges: Range[]) => void;
  onArmedChange?: (armed: ScheduleSelection | null) => void;
  /** Un glisser en diagonale a peint (ou sauté) sur plusieurs colonnes (D13). */
  onMultiColumnPaint?: (result: MultiColumnPaintResult) => void;
  /** Sondage avec réponses déjà reçues (D34h) : grille figée, aucune interaction. */
  readOnly?: boolean;
  /** Minute à partir de laquelle une date est "passée" (D32) ; seule la date du jour est concernée. */
  todayCutoff?: { date: string; minute: number } | null;
  className?: string;
}

function useIsPointerCoarse(): boolean {
  return useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(pointer: coarse)").matches;
  }, []);
}

export const ScheduleGridV2 = forwardRef<
  ScheduleGridV2Handle,
  ScheduleGridV2Props
>(function ScheduleGridV2(
  {
    dates,
    dayRanges,
    step,
    visibleStartMinute,
    visibleEndMinute,
    selected,
    onSelect,
    onRangesChange,
    onArmedChange,
    onMultiColumnPaint,
    readOnly = false,
    todayCutoff = null,
    className,
  },
  ref,
) {
  const { t, i18n } = useTranslation("creator");
  const pointerCoarse = useIsPointerCoarse();
  const scrollerRef = useRef<HTMLDivElement>(null);

  const dims = computeGridDimensions(step, pointerCoarse);
  const rows = rowCount(visibleStartMinute, visibleEndMinute, step);

  const gesture = usePaintGesture({
    stepMinutes: step,
    pxPerMin: dims.pxPerMin,
    contentTopMinute: visibleStartMinute,
    dates,
    getRanges: (date) => dayRanges[date] ?? [],
    onRangesChange: readOnly ? () => {} : onRangesChange,
    onSelect,
    onMultiColumnPaint: readOnly ? undefined : onMultiColumnPaint,
    selected,
  });

  React.useEffect(() => {
    onArmedChange?.(gesture.armed);
  }, [gesture.armed]);

  // Recréé à chaque nouveau pas : le parent qui l'appelle depuis son
  // propre `useLayoutEffect` (ancrage D33o) obtient donc déjà le nouveau
  // `pxPerMin` — les handles des enfants sont posés avant les effets de
  // mise en page du parent.
  useImperativeHandle(ref, () => {
    // Hauteur des lignes visibles : l'en-tête collant masque le haut du
    // conteneur, le centre utile est celui de ce qui reste dessous.
    const rowsViewportHeight = (el: HTMLDivElement) =>
      Math.max(0, el.clientHeight - HEADER_HEIGHT);
    return {
      scrollToMinutes(minute: number, behavior: ScrollBehavior = "smooth") {
        const el = scrollerRef.current;
        if (!el) return;
        const top = scrollTopToKeepCentered(
          minute,
          visibleStartMinute,
          dims.pxPerMin,
          rowsViewportHeight(el),
        );
        if (behavior === "smooth" && typeof el.scrollTo === "function") {
          el.scrollTo({ top, behavior });
        } else {
          el.scrollTop = top;
        }
      },
      getCenterMinute() {
        const el = scrollerRef.current;
        if (!el) return null;
        return centerMinuteFromScroll(
          el.scrollTop,
          visibleStartMinute,
          dims.pxPerMin,
          rowsViewportHeight(el),
        );
      },
    };
  }, [dims.pxPerMin, visibleStartMinute]);

  const locale = localeTag(i18n.language);
  const dayFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric" }),
    [locale],
  );
  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: "short" }),
    [locale],
  );
  const fullDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    [locale],
  );
  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }),
    [locale],
  );

  function formatMinute(minute: number): string {
    const d = new Date(2000, 0, 1, 0, minute);
    return timeFormatter.format(d);
  }
  function parseDate(date: string): Date {
    const [y, m, d] = date.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  // Lignes de fond : un dégradé unique posé sur la grille entière (D33d),
  // une ligne toutes les `step` minutes, plus marquée toutes les 60 min.
  const backgroundImage = [
    `repeating-linear-gradient(to bottom, rgba(120,120,130,0.35) 0 1px, transparent 1px ${dims.rowHeight * (60 / step)}px)`,
    `repeating-linear-gradient(to bottom, rgba(120,120,130,0.15) 0 1px, transparent 1px ${dims.rowHeight}px)`,
  ].join(", ");

  return (
    <div
      ref={scrollerRef}
      data-testid="schedule-v2-scroller"
      data-pointer={pointerCoarse ? "coarse" : "fine"}
      className={`relative overflow-auto border border-border rounded-lg bg-card ${className ?? ""}`}
      style={{ height: "100%", touchAction: "pan-y" }}
    >
      <div
        data-testid="schedule-v2-grid"
        style={{
          display: "grid",
          gridTemplateColumns: `${dims.hourColWidth}px repeat(${dates.length}, minmax(${dims.dayMinWidth}px, 1fr))`,
          gridTemplateRows: `${HEADER_HEIGHT}px repeat(${rows}, ${dims.rowHeight}px)`,
          width: "max-content",
          minWidth: "100%",
          backgroundImage,
          backgroundPosition: `0 ${HEADER_HEIGHT}px`,
          backgroundRepeat: "repeat",
        }}
      >
        {/* Case d'angle (D33c : au-dessus des deux autres zones collantes). */}
        <div
          style={{
            gridRow: 1,
            gridColumn: 1,
            position: "sticky",
            top: 0,
            left: 0,
            zIndex: 3,
          }}
          className="bg-card border-b border-r border-border"
        />

        {/* En-têtes de jours (D33h). */}
        {dates.map((date, i) => {
          const d = parseDate(date);
          const prevDate = i > 0 ? parseDate(dates[i - 1]) : null;
          const showMonth =
            i === 0 ||
            (prevDate !== null && prevDate.getMonth() !== d.getMonth());
          return (
            <div
              key={date}
              data-testid={`schedule-v2-day-header-${date}`}
              title={fullDateFormatter.format(d)}
              style={{
                gridRow: 1,
                gridColumn: i + 2,
                position: "sticky",
                top: 0,
                zIndex: 2,
              }}
              className="bg-card border-b border-r border-border flex flex-col items-center justify-center overflow-hidden px-1"
            >
              <span className="text-xs font-medium truncate w-full text-center">
                {dayFormatter.format(d)}
              </span>
              {showMonth && (
                <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                  {monthFormatter.format(d)}
                </span>
              )}
            </div>
          );
        })}

        {/* Colonne des heures (D33c, D33g). */}
        <div
          style={{
            gridRow: `2 / span ${rows}`,
            gridColumn: 1,
            position: "sticky",
            left: 0,
            zIndex: 1,
          }}
          className="bg-card border-r border-border relative"
        >
          {Array.from(
            { length: rows + 1 },
            (_, i) => visibleStartMinute + i * step,
          )
            // D33i : pas < 60 → étiquette seulement sur les demi-heures
            // pleines (09:00, 09:30 — pas 09:15/09:45) ; pas ≥ 60 → une
            // étiquette par ligne (chaque ligne EST déjà une heure ou plus).
            .filter((minute) => step >= 60 || minute % 30 === 0)
            .map((minute) => {
              const isFullHour = minute % 60 === 0;
              return (
                <span
                  key={minute}
                  data-testid={`schedule-v2-hour-label-${minute}`}
                  data-minute={minute}
                  style={{
                    position: "absolute",
                    top: (minute - visibleStartMinute) * dims.pxPerMin,
                    right: 6,
                    transform: "translateY(-50%)",
                  }}
                  className={
                    isFullHour
                      ? "text-xs text-muted-foreground"
                      : "text-[10px] text-muted-foreground/70"
                  }
                >
                  {formatMinute(minute)}
                </span>
              );
            })}
        </div>

        {/* Colonnes de jour : une seule cellule de grille par jour, plages en absolu (D33d). */}
        {dates.map((date, i) => {
          const ranges = dayRanges[date] ?? [];
          const isToday = todayCutoff?.date === date;
          return (
            <div
              key={date}
              data-testid={`schedule-v2-day-col-${date}`}
              data-date={date}
              data-day-column="true"
              style={{
                gridRow: `2 / span ${rows}`,
                gridColumn: i + 2,
                position: "relative",
              }}
              className="border-r border-border"
              onPointerDown={(e) => {
                if (readOnly) return;
                if ((e.target as HTMLElement).closest("[data-range-index]"))
                  return;
                gesture.handlers.onPointerDown(e, date, null);
              }}
              onPointerMove={gesture.handlers.onPointerMove}
              onPointerUp={gesture.handlers.onPointerUp}
              onPointerCancel={gesture.handlers.onPointerCancel}
            >
              {ranges.length === 0 && (
                <div className="absolute top-1 left-1 right-1 text-[11px] text-muted-foreground pointer-events-none">
                  {t("schedule_v2.all_day_label")}
                </div>
              )}
              {todayCutoff &&
                isToday &&
                todayCutoff.minute > visibleStartMinute && (
                  <div
                    data-testid={`schedule-v2-past-overlay-${date}`}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: Math.max(
                        0,
                        (todayCutoff.minute - visibleStartMinute) *
                          dims.pxPerMin,
                      ),
                      pointerEvents: "none",
                    }}
                    className="bg-muted/60"
                  />
                )}
              {ranges.map((range, idx) => {
                const isSelected =
                  selected?.date === date && selected.index === idx;
                const top = (range.start - visibleStartMinute) * dims.pxPerMin;
                const height = (range.end - range.start) * dims.pxPerMin;
                const label = `${formatMinute(range.start)} – ${formatMinute(range.end)}`;
                return (
                  <button
                    key={`${range.start}-${range.end}`}
                    type="button"
                    data-testid="schedule-v2-range"
                    data-range-index={idx}
                    data-date={date}
                    data-start={range.start}
                    data-end={range.end}
                    data-selected={isSelected ? "true" : "false"}
                    aria-pressed={isSelected}
                    aria-label={t("schedule_v2.range_selected_aria", {
                      day: dayFormatter.format(parseDate(date)),
                      range: label,
                    })}
                    tabIndex={0}
                    title={label}
                    style={{
                      position: "absolute",
                      top,
                      height,
                      left: 3,
                      right: 3,
                    }}
                    className={`rounded-md text-[11px] font-medium flex items-center justify-center transition-colors ${
                      isSelected
                        ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-1"
                        : "bg-primary/80 text-primary-foreground hover:bg-primary"
                    }`}
                    onPointerDown={(e) => {
                      if (readOnly) return;
                      e.stopPropagation();
                      gesture.handlers.onPointerDown(e, date, idx);
                    }}
                    onPointerMove={gesture.handlers.onPointerMove}
                    onPointerUp={gesture.handlers.onPointerUp}
                    onPointerCancel={gesture.handlers.onPointerCancel}
                    onKeyDown={(e) => {
                      // D17 : le bouton est focusable (Tab) mais n'a pas de
                      // pointerdown/up au clavier — sans ceci, Entrée/Espace
                      // ne sélectionnaient jamais la plage, rendant Suppr et
                      // les flèches inaccessibles au clavier seul.
                      if (readOnly) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect({ date, index: idx });
                      }
                    }}
                  >
                    <span className="truncate px-1">{label}</span>
                  </button>
                );
              })}
              {gesture.ghosts
                .filter((g) => g.date === date)
                .map((g, gi) => (
                  <div
                    key={`ghost-${gi}`}
                    data-testid="schedule-v2-ghost"
                    data-skipped={g.multi && g.blocked ? "true" : "false"}
                    data-truncated={g.truncated ? "true" : "false"}
                    style={{
                      position: "absolute",
                      top:
                        (Math.min(g.start, g.end) - visibleStartMinute) *
                        dims.pxPerMin,
                      height: Math.abs(g.end - g.start) * dims.pxPerMin,
                      left: 3,
                      right: 3,
                      pointerEvents: "none",
                      backgroundImage:
                        g.multi && g.blocked
                          ? "repeating-linear-gradient(45deg, rgba(220,38,38,0.35) 0 6px, transparent 6px 12px)"
                          : undefined,
                    }}
                    className={`rounded-md border-2 border-dashed ${
                      g.blocked
                        ? "border-destructive bg-destructive/10"
                        : g.truncated
                          ? "border-warning bg-primary/20"
                          : "border-primary bg-primary/20"
                    }`}
                  >
                    {/* Design A (D12) : le geste s'arrête au premier obstacle — le
                          rendre visible PENDANT le glisser plutôt que silencieux. */}
                    {g.truncated && (
                      <div
                        data-testid="schedule-v2-ghost-truncated-hint"
                        className="absolute inset-x-0 text-[10px] leading-tight text-warning-foreground bg-warning/90 rounded px-1 text-center pointer-events-none"
                        style={
                          g.truncatedEdge === "start"
                            ? { top: 0 }
                            : { bottom: 0 }
                        }
                      >
                        {t("schedule_v2.paint_truncated_hint")}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
});

/** Bouton × pour supprimer la plage sélectionnée (D11). Exporté à part pour la barre d'actions. */
export function DeleteRangeIcon(props: React.SVGProps<SVGSVGElement>) {
  return <X {...props} />;
}
